"use client";

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Header from "~/components/patterns/header";
import { GiftCardPreview } from "~/components/gift-card/gift-card-preview";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { Label } from "~/components/ui/label";
import { capturePostHogEvent } from "~/lib/posthog/client";
import {
  giftCardPurchaseSchema,
  GIFT_CARD_DRAFT_KEY,
  readGiftCardDraft,
  type GiftCardPurchase,
} from "~/lib/gift-card";

export default function GiftCardPage() {
  const router = useRouter();
  const started = useRef(false);
  const opened = useRef(false);
  const [customAmount, setCustomAmount] = useState(false);
  const [personalize, setPersonalize] = useState(false);
  const [storageError, setStorageError] = useState("");
  const form = useForm<
    z.input<typeof giftCardPurchaseSchema>,
    unknown,
    GiftCardPurchase
  >({
    resolver: zodResolver(giftCardPurchaseSchema),
    defaultValues: {
      valueInEur: 50,
      deliveryMode: "buyer",
      recipientEmail: "",
      recipientName: "",
      personalMessage: "",
      purchaserEmail: "",
      purchaserName: "",
    },
  });
  const values = form.watch();
  const deliveryMode = values.deliveryMode ?? "buyer";

  useEffect(() => {
    const draft = readGiftCardDraft();
    if (draft) {
      form.reset(draft);
      setCustomAmount(![50, 100, 150, 200].includes(draft.valueInEur));
      setPersonalize(
        Boolean(
          draft.recipientName || draft.personalMessage || draft.purchaserName,
        ),
      );
      if (draft.isAnonymous) form.setValue("purchaserName", "");
    }
    if (!opened.current) {
      opened.current = true;
      capturePostHogEvent("gift_card_opened");
    }
  }, [form]);

  const startForm = () => {
    if (started.current) return;
    started.current = true;
    capturePostHogEvent("gift_card_form_started");
  };

  const continueToPayment = form.handleSubmit(
    (data) => {
      const draft = {
        ...data,
        isAnonymous: !data.purchaserName?.trim(),
        recipientEmail:
          data.deliveryMode === "buyer" ? "" : data.recipientEmail,
        purchaserName: data.purchaserName?.trim() || "",
      };
      try {
        sessionStorage.setItem(GIFT_CARD_DRAFT_KEY, JSON.stringify(draft));
        router.push("/gift-card/pay");
      } catch {
        setStorageError(
          "Pregledač ne može da sačuva podatke za plaćanje. Dozvoli podatke sajta ili probaj drugi pregledač.",
        );
      }
    },
    (errors) => {
      capturePostHogEvent("gift_card_validation_failed", {
        fields: Object.keys(errors),
        delivery_mode: deliveryMode,
      });
      setStorageError("");
    },
  );

  return (
    <div>
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-16">
        <div className="mb-6 max-w-2xl space-y-3">
          <h1 className="h1-marketing">Pokloni razgovor.</h1>
          <p className="text-muted-foreground text-lg">
            Poklon vaučer za online psihoterapiju.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] lg:gap-16">
          <aside className="space-y-4 lg:sticky lg:top-24 lg:col-start-2 lg:row-start-1 lg:self-start">
            <GiftCardPreview
              amount={
                Number.isFinite(values.valueInEur) ? values.valueInEur : 0
              }
              recipientName={values.recipientName}
              senderName={values.purchaserName}
              message={values.personalMessage}
            />
            <div className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <span>Terapeut po izboru</span>
              <span>Aktivacija u roku od 12 meseci</span>
            </div>
            <details className="text-muted-foreground text-sm leading-relaxed">
              <summary className="text-foreground cursor-pointer font-semibold">
                Kako se koristi?
              </summary>
              <p className="mt-3">
                Primalac aktivira vaučer na svom nalogu. Iznos postaje kredit za
                sesije, a ostatak ostaje na nalogu. Ako sesija košta više,
                razlika se doplaćuje.
              </p>
              <p className="mt-2">Vaučer može da aktivira samo jedan nalog.</p>
              <Link
                href="/terapeuti"
                className="text-brand mt-3 inline-block font-semibold underline underline-offset-4"
              >
                Terapeuti i cene
              </Link>
            </details>
          </aside>

          <form
            noValidate
            onSubmit={continueToPayment}
            onFocusCapture={startForm}
            onChangeCapture={startForm}
            className="space-y-6 lg:col-start-1 lg:row-start-1"
          >
            <fieldset className="space-y-4">
              <legend className="mb-4 text-lg font-bold">
                Vrednost poklona
              </legend>
              <div className="grid grid-cols-4 gap-2">
                {[50, 100, 150, 200].map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    variant={
                      !customAmount && values.valueInEur === amount
                        ? "default"
                        : "outline"
                    }
                    aria-pressed={!customAmount && values.valueInEur === amount}
                    onClick={() => {
                      startForm();
                      setCustomAmount(false);
                      form.setValue("valueInEur", amount, {
                        shouldValidate: true,
                      });
                    }}
                  >
                    {amount} €
                  </Button>
                ))}
              </div>
              <button
                type="button"
                className="text-brand text-sm underline underline-offset-4"
                aria-expanded={customAmount}
                aria-controls="gift-custom-amount"
                onClick={() => setCustomAmount(!customAmount)}
              >
                Drugi iznos
              </button>
              {customAmount && (
                <div id="gift-custom-amount" className="space-y-2">
                  <Label htmlFor="gift-amount">Iznos u evrima</Label>
                  <Input
                    id="gift-amount"
                    type="number"
                    min={50}
                    max={1000}
                    step={0.01}
                    {...form.register("valueInEur", { valueAsNumber: true })}
                    aria-invalid={Boolean(form.formState.errors.valueInEur)}
                    aria-describedby="gift-amount-error"
                  />
                </div>
              )}
              <p
                id="gift-amount-error"
                className="text-destructive text-sm"
                role="alert"
              >
                {form.formState.errors.valueInEur?.message}
              </p>
            </fieldset>

            <div className="space-y-2">
              <Label htmlFor="gift-purchaser-email">Tvoj email</Label>
              <Input
                id="gift-purchaser-email"
                type="email"
                autoComplete="email"
                {...form.register("purchaserEmail")}
                aria-invalid={Boolean(form.formState.errors.purchaserEmail)}
                aria-describedby="gift-purchaser-error gift-purchaser-help"
              />
              <p
                id="gift-purchaser-help"
                className="text-muted-foreground text-sm"
              >
                {deliveryMode === "buyer"
                  ? "Vaučer stiže na tvoj email. Prosledi kada želiš."
                  : "Za potvrdu kupovine."}
              </p>
              <p
                id="gift-purchaser-error"
                className="text-destructive text-sm"
                role="alert"
              >
                {form.formState.errors.purchaserEmail?.message}
              </p>
            </div>
            <label className="flex cursor-pointer items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={deliveryMode === "recipient"}
                onChange={(event) =>
                  form.setValue(
                    "deliveryMode",
                    event.target.checked ? "recipient" : "buyer",
                  )
                }
                className="accent-brand"
              />
              Pošalji direktno primaocu
            </label>
            {deliveryMode === "recipient" && (
              <div className="space-y-2">
                <Label htmlFor="gift-recipient-email">Email primaoca</Label>
                <Input
                  id="gift-recipient-email"
                  type="email"
                  autoComplete="off"
                  {...form.register("recipientEmail")}
                  aria-invalid={Boolean(form.formState.errors.recipientEmail)}
                  aria-describedby="gift-recipient-error gift-recipient-help"
                />
                <p
                  id="gift-recipient-help"
                  className="text-muted-foreground text-sm"
                >
                  Šaljemo odmah posle plaćanja. Primalac aktivira vaučer sa ovom
                  adresom.
                </p>
                <p
                  id="gift-recipient-error"
                  className="text-destructive text-sm"
                  role="alert"
                >
                  {form.formState.errors.recipientEmail?.message}
                </p>
              </div>
            )}
            <div>
              <button
                type="button"
                className="text-brand text-sm font-semibold underline underline-offset-4"
                aria-expanded={personalize}
                aria-controls="gift-personalization"
                onClick={() => setPersonalize(!personalize)}
              >
                Dodaj poruku i potpis
              </button>
              <div
                id="gift-personalization"
                hidden={!personalize}
                className="mt-4 space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="gift-recipient-name">
                    Ime primaoca (neobavezno)
                  </Label>
                  <Input
                    id="gift-recipient-name"
                    maxLength={100}
                    autoComplete="off"
                    {...form.register("recipientName")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gift-message">Poruka (neobavezno)</Label>
                  <Textarea
                    id="gift-message"
                    rows={3}
                    maxLength={500}
                    {...form.register("personalMessage")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gift-purchaser-name">
                    Tvoj potpis (neobavezno)
                  </Label>
                  <Input
                    id="gift-purchaser-name"
                    maxLength={100}
                    autoComplete="name"
                    {...form.register("purchaserName")}
                  />
                </div>
              </div>
            </div>
            {storageError && (
              <p role="alert" className="text-destructive text-sm">
                {storageError}
              </p>
            )}
            <div className="flex items-center justify-between gap-4">
              <span className="text-lg font-bold">
                {Number.isFinite(values.valueInEur)
                  ? `${values.valueInEur} €`
                  : "Izaberi iznos"}
              </span>
              <Button type="submit">Nastavi na plaćanje</Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
